import _ from 'lodash';
import numeral from 'numeral';
import * as CompanyUtils from './company';
import * as TransactionUtils from './transaction';
import { TWIC_ACCOUNT_TYPES } from 'constants/accounts';
import moment from 'moment';
import lookup from 'country-code-lookup';
import { COUNTRIES } from 'constants/countries';

export function getLast4CardDigit({ employee }: $TSFixMe) {
  if (
    !employee?.settings ||
    !employee?.settings.payment ||
    !employee?.settings.payment.token
  ) {
    return null;
  }

  return employee.settings.payment.last4;
}

export function getFormattedAddress(employee: $TSFixMe) {
  if (!employee.settings.address || !employee.settings.address.street) {
    return null;
  }

  const {
    address: { street, street_ext, locality, state, zip },
  } = employee.settings;

  return `${street}${
    street_ext ? ` ${street_ext}` : ''
  },\n${locality}, ${state} ${zip}`;
}

export function getApplicableWalletsForSubCategories(
  employee_wallets: $TSFixMe,
  subcategories: Array<string> = [],
  vendor?: string
) {
  return employee_wallets
    .filter((wallet: $TSFixMe) => {
      const wallet_config = wallet.company_wallet_configuration;
      if (
        (vendor &&
          wallet_config.blacklisted_vendors &&
          wallet_config.blacklisted_vendors.indexOf(vendor) !== -1) ||
        !wallet_config.categories?.length
      ) {
        return false;
      }

      const wallet_subcategories: $TSFixMe = [];
      _.map(wallet_config.categories, (c) => {
        const subs = _.filter(c.subcategories, { eligible: true }).map(
          (e) => e.value
        );
        wallet_subcategories.push(...subs);
      });

      return (
        _.intersection(wallet_subcategories, subcategories).length > 0 ||
        wallet_config.categories.length === 0
      );
    })
    .sort(
      (a: $TSFixMe, b: $TSFixMe) =>
        a.company_wallet_configuration.priority -
        b.company_wallet_configuration.priority
    );
}

export function getEligibleCategoriesForWallet(
  config_id: $TSFixMe,
  company_wallet_configurations: $TSFixMe
) {
  const config = _.find(
    company_wallet_configurations,
    (config) => config.id === config_id
  );

  const others = {
    others: { id: 'others', name: 'others' },
  };
  if (!config || !_.size(config.categories)) {
    return others;
  }
  const categories = {};
  config.categories.forEach((c: $TSFixMe) => {
    c.subcategories.forEach((s: $TSFixMe) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      if (s.eligible && !categories[c.id]) {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        categories[c.id] = c;
      }
    });
  });
  return _.orderBy(categories, 'name');
}

type WalletType = {
  amount: number;
};

export function getTotalAmountInWallets(wallets: Array<WalletType>) {
  return (
    (wallets &&
      wallets.length &&
      wallets.reduce((sum, wallet) => sum + wallet.amount, 0)) ||
    0
  );
}

export function getTotalAmountInAllWallet(employee: $TSFixMe) {
  if (!employee.employee_wallets || !employee.employee_wallets.length) {
    return 0;
  }

  return getTotalAmountInWallets(employee.employee_wallets);
}

export function getTotalInAllWallet(
  employee: $TSFixMe,
  company: $TSFixMe,
  format = false
) {
  const total_wallet_amount = getTotalAmountInAllWallet(employee);
  const amount_to_points_ratio = CompanyUtils.getAmountToPointRatio(company);
  const { display_as_amount } = company.features.stipend_config;
  const { currency } = employee.settings;

  let value = numeral(total_wallet_amount)
    .multiply(amount_to_points_ratio)
    .value();

  if (display_as_amount) {
    value = total_wallet_amount;
  }

  if (format) {
    return TransactionUtils.formatAmountOrPoints({ company, value, currency });
  }

  return value;
}

export function getTotalPointsInWallets(wallets: $TSFixMe, company: $TSFixMe) {
  const total_wallet_amount = getTotalAmountInWallets(wallets);
  const amount_to_points_ratio = CompanyUtils.getAmountToPointRatio(company);

  return numeral(total_wallet_amount).multiply(amount_to_points_ratio).value();
}

export function allowMarketplace(employee: $TSFixMe, company: $TSFixMe) {
  return company.features.marketplace;
}

export function isInUnitedStates(employee: { settings: { country: string } }) {
  return employee.settings.country === COUNTRIES.US;
}

export function isApplicableForTwicCard(employee: $TSFixMe, company: $TSFixMe) {
  const isCardEnabled =
    company.settings.stripe_virtual_card_enabled ||
    employee.settings.stripe_virtual_card_enabled ||
    company.settings.stripe_physical_card_enabled ||
    employee.settings.stripe_physical_card_enabled;

  return isInUnitedStates(employee) && isCardEnabled;
}

export function hasPersonalAvatar(employee: $TSFixMe) {
  const DEFAULT =
    'https://s3-us-west-1.amazonaws.com/twic.ai/avatar/default_avatar.png';
  return employee.avatar_url !== DEFAULT;
}

export function getInitial(employee: $TSFixMe) {
  return _.toUpper(`${employee.first_name[0]}${employee.last_name[0]}`);
}

export function getConnectedBankAccountName(connected_account: $TSFixMe) {
  if (!connected_account || connected_account.status !== 'active') {
    return '';
  }
  const { account_type_code, last4 } = connected_account;
  return `${
    account_type_code ? `${account_type_code} account` : 'Account'
  } ending in *${last4}`;
}

export function getPretaxShippingAddress(shipping_address: $TSFixMe) {
  const { line1, line2, city, state, zip } = shipping_address;
  return `${line1} ${line2}, ${city}, ${state} ${zip}`;
}

/**
 * Determine if a user has pretax accounts.
 *
 * @param {Object} params - Params needed to determine pretax status.
 * @param {Object} params.employee - Employee object.
 * @param {Object} params.pretax_accounts - Pretax accounts array.
 * @returns {boolean} True if has pretax account, false otherwise
 */
export const hasPretaxAccounts = ({ employee, pretax_accounts }: $TSFixMe) =>
  employee.settings?.is_cdh_enabled && pretax_accounts.length > 0;

/**
 * Get employee country in lowercase format.
 *
 * @param {Object} params - Params needed to get country.
 * @returns {string} Country in lowercase.
 */
export const getCountry = ({ employee }: $TSFixMe) =>
  employee && employee.settings && _.lowerCase(employee.settings.country);

export const selectWalletFromCompanyConfigs = ({
  id,
  employee_wallets_used,
  company_wallet_configurations,
  company_wallet_configuration_id,
}: $TSFixMe) => {
  const configuration_id =
    company_wallet_configuration_id ||
    (employee_wallets_used &&
      employee_wallets_used.length &&
      employee_wallets_used.find(
        ({ employee_wallet_id }: { employee_wallet_id: string }) =>
          employee_wallet_id === id
      )?.company_wallet_configuration_id);

  return (
    configuration_id &&
    company_wallet_configurations?.length &&
    company_wallet_configurations.find(
      ({ id }: { id: string }) => id === configuration_id
    )
  );
};

export const selectDefaultWallet = <
  Wallet extends {
    company_wallet_configuration: {
      is_twic_store_enabled?: boolean;
    };
    id: string;
    wallet_type_id: string;
  }
>({
  employee_wallets,
  default_employee_wallet_id,
}: {
  employee_wallets: Array<Wallet>;
  default_employee_wallet_id?: string;
}) => {
  const wellness_wallet = employee_wallets.find(({ wallet_type_id, id }) =>
    default_employee_wallet_id
      ? id === default_employee_wallet_id
      : wallet_type_id === TWIC_ACCOUNT_TYPES.WELLNESS
  );

  // default to wellness wallet if it exists
  if (wellness_wallet) {
    return wellness_wallet;
  }

  // otherwise use the 1st wallet that is store-enabled
  const store_enabled_wallet = employee_wallets.find(
    (w: Wallet) => w.company_wallet_configuration.is_twic_store_enabled
  );

  // final fallback is first wallet of any type
  return store_enabled_wallet || employee_wallets[0];
};

export const getAllCategories = ({
  employee_wallets,
}: {
  employee_wallets: Array<{
    company_wallet_configuration: { categories: Array<string> };
  }>;
}) =>
  employee_wallets.flatMap(
    ({ company_wallet_configuration }) =>
      company_wallet_configuration.categories
  );

export const isTwicCardEnabled = ({ employee, company }: $TSFixMe) => {
  const {
    employee_wallets,
    settings: {
      country,
      stripe_virtual_card_enabled: employee_stripe_virtual_card_enabled,
    },
  } = employee;
  const {
    settings: { stripe_virtual_card_enabled, stripe_physical_card_enabled },
  } = company;

  const is_american = country === 'us';
  const is_card_payment_allowed = employee_wallets.some(
    ({
      company_wallet_configuration: { is_twic_card_payment_allowed },
    }: $TSFixMe) => is_twic_card_payment_allowed
  );

  return (
    is_american &&
    is_card_payment_allowed &&
    (employee_stripe_virtual_card_enabled ||
      stripe_virtual_card_enabled ||
      stripe_physical_card_enabled)
  );
};

export const getActiveEmployeeWallets = <
  T extends {
    company_wallet_configuration_id: string;
    visible_to_employee: boolean;
  }
>({
  employer_sponsored_wallets,
  company_wallet_configurations,
  store_enabled,
}: {
  employer_sponsored_wallets: Array<T>;
  company_wallet_configurations: Array<{
    id: string;
    start_date: string;
    is_twic_store_enabled?: boolean;
  }>;
  store_enabled?: boolean;
}) => {
  const today = moment();

  return employer_sponsored_wallets
    .filter(({ visible_to_employee }) => visible_to_employee)
    .filter(({ company_wallet_configuration_id }) => {
      const company_wallet_configuration = company_wallet_configurations.find(
        (company_wallet_configuration) =>
          company_wallet_configuration.id === company_wallet_configuration_id
      );
      return (
        today.isSameOrAfter(moment(company_wallet_configuration?.start_date)) &&
        (_.isBoolean(store_enabled)
          ? store_enabled ===
            company_wallet_configuration?.is_twic_store_enabled
          : true)
      );
    });
};

export const renderCountryName = (input_country_code = ''): string => {
  try {
    const code = input_country_code?.toLowerCase();
    const found_country = lookup.byIso(code);
    return found_country ? found_country.country : code;
  } catch (e) {
    return '';
  }
};
